import { EkranStartowy, LogoInsolitum, ThemeToggle } from '@insolitum/ui';
import {
  Award,
  BarChart3,
  LayoutDashboard,
  Building2,
  Wallet,
  User,
  Users,
  Settings,
  Menu,
  X,
  Handshake,
  GraduationCap,
  HandCoins,
  ChevronLeft,
  PanelLeftClose,
  PanelLeftOpen,
  TrendingUp,
  Ticket,
  Package,
  Tag,
  Map,
  MonitorPlay,
  Search as SearchIcon,
  type LucideIcon,
} from 'lucide-react';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';

import { ImpersonationBanner } from '../components/ImpersonationBanner';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { UserMenu } from '../components/UserMenu';
import { CommandPalette, useCommandPalette } from '../components/aurora/CommandPalette';
import { WejscieNaKontoKlienta } from '../components/support-access/WejscieNaKontoKlienta';
import { useFirmaPokazowa } from '../hooks/useFirmaPokazowa';
import { usePartnerContext } from '../hooks/usePartnerContext';
import { useSidebarCollapse } from '../hooks/useSidebarCollapse';
import { useAuth } from '../providers/AuthProvider';

interface PartnerNavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  end?: boolean;
}

export function PartnerLayout() {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isCollapsed: collapsed, toggleCollapse } = useSidebarCollapse(
    'partner-sidebar-collapsed'
  );
  const { user, organizations, switchOrganization, signOut } = useAuth();
  // Wejście na konto klienta stoi w pasku górnym, więc kontekst partnera jest
  // potrzebny w layoucie: bez `partnerOrgId` baza nie wie, czyjej zgody dotyczy
  // kod, gdy panel oglądany jest przez super-admina w trybie podglądu.
  const { partnerOrgId } = usePartnerContext();
  const firmaPokazowa = useFirmaPokazowa();
  const navigate = useNavigate();

  // „Powrót do Admina" ma sens tylko dla super-admina (impersonacja) lub konta,
  // które poza rolą partnera administruje prawdziwą organizacją-tenantem.
  // Czysty partner klikając /admin lądował w bezużytecznym panelu tenant-admin
  // własnej organizacji partnerskiej — bez drogi powrotnej.
  // Organizacja partnera i firma pokazowa się nie liczą: pierwsza najemcą nie
  // jest (członek zespołu z rolą `admin` lądował w jej „panelu najemcy”), a do
  // drugiej prowadzi osobna pozycja „Konto demo”.
  const canReturnToAdmin =
    user?.is_super_admin ||
    organizations.some(
      (o) =>
        (o.role === 'owner' || o.role === 'admin') &&
        o.organization_id !== partnerOrgId &&
        o.organization_id !== firmaPokazowa.organizationId
    );

  // Firma pokazowa w tej samej sesji, na każdej domenie (tak jak wejście na
  // konto klienta): przełączenie organizacji i panel firmy pod `/admin`.
  const otworzFirmePokazowa = () => {
    if (!firmaPokazowa.organizationId) return;
    switchOrganization(firmaPokazowa.organizationId);
    setSidebarOpen(false);
    navigate('/admin');
  };

  // Grupy nawigacji 1:1 z prototypem V2 (Aurora DS.html, VIEW: PARTNER): jedna
  // grupa „Przegląd" z płaską listą Dashboard → Moje moduły (te same trasy
  // i ikony co dotąd); pozycje spoza prototypu zostają w grupie „Konto".
  const navGroups: { label: string; items: PartnerNavItem[] }[] = [
    {
      label: t('partner.nav.overview', 'Przegląd'),
      items: [
        {
          name: t('partner.nav.dashboard', 'Dashboard'),
          href: '/partner',
          icon: LayoutDashboard,
          end: true,
        },
        { name: t('partner.myClients', 'Moi klienci'), href: '/partner/clients', icon: Building2 },
        {
          name: t('partner.clientAnalytics', 'Analityka klientów'),
          href: '/partner/analytics',
          icon: BarChart3,
        },
        {
          name: t('partner.nav.commissions', 'Prowizje'),
          href: '/partner/commissions',
          icon: Wallet,
        },
        { name: t('partner.pricing', 'Oferta i cennik'), href: '/partner/pricing', icon: Tag },
        {
          name: t('partner.territory', 'Obszar działania'),
          href: '/partner/territory',
          icon: Map,
        },
        { name: t('partner.nav.reports', 'Raporty'), href: '/partner/reports', icon: TrendingUp },
        { name: t('partner.team', 'Zespół'), href: '/partner/team', icon: Users },
        { name: t('partner.modules', 'Moje moduły'), href: '/partner/modules', icon: Package },
      ],
    },
    {
      label: t('partner.nav.account', 'Konto'),
      items: [
        // Akademia i Moje premie przyszły tu z panelu admina tenanta: Akademia
        // to onboarding partnerski (postęp pod rekordem `partners`), a premie
        // przyznaje partner ze swojej prowizji — tenant nie ma tu nic swojego.
        { name: 'Akademia Nexusa', href: '/partner/akademia', icon: GraduationCap },
        // Certyfikaty modułowe: kurs → egzamin → praktyka → wejście do organu
        // na koncie klienta (fale P5-P7 planu 45).
        { name: 'Certyfikaty', href: '/partner/certyfikaty', icon: Award },
        { name: 'Moje premie', href: '/partner/premie', icon: HandCoins },
        { name: t('partner.nav.profile', 'Mój profil'), href: '/partner/profile', icon: User },
        { name: 'Wsparcie', href: '/partner/support', icon: Ticket },
        {
          name: t('partner.nav.settings', 'Ustawienia'),
          href: '/partner/settings',
          icon: Settings,
        },
      ],
    },
  ];
  // Płaska lista (ta sama kolejność co dotąd) — dla ⌘K i okruszków topbara.
  const navigation = navGroups.flatMap((g) => g.items.map((i) => ({ ...i, section: g.label })));

  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useCommandPalette();
  // Okruszki topbara — nazwa bieżącej strony (czysto wizualne).
  const currentPage =
    navigation
      .filter((i) => (i.end ? location.pathname === i.href : location.pathname.startsWith(i.href)))
      .sort((a, b) => b.href.length - a.href.length)[0]?.name ??
    t('partner.nav.dashboard', 'Dashboard');

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="role-partner min-h-screen bg-aurora-bg">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 bg-aurora-bg-subtle border-r border-aurora-border transform transition-all duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${collapsed ? 'w-16' : 'w-64'}`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar top — .sidebar__top: 64 px z dolną linią, wcięcie 12 —
              ta sama oś co nagłówek paska organu i panelu super-admina. */}
          <div className="sidebar__top">
            <span className={`sidebar__sygnet ${collapsed ? 'mx-auto' : ''}`}>
              {/* Zwinięty pasek nie ma napisu obok — wtedy znak sam niesie
                  nazwę (jak w SuperAdminLayout). */}
              <LogoInsolitum uklad="sygnet" tytul={collapsed ? 'Insolitum Universe' : null} />
            </span>
            {!collapsed && (
              <span className="sidebar__id">
                <span className="sidebar__name">Panel Partnera</span>
                {/* Rola bez słowa marki: „Insolitum” krojem interfejsu tuż
                    przy gołym sygnecie udawałoby napis znaku
                    (ZNAK-W-SYSTEMIE §3 p. 5). */}
                <span className="sidebar__role">{t('partner.nav.role', 'Program partnerski')}</span>
              </span>
            )}
            <button
              onClick={() => setSidebarOpen(false)}
              className="iconbtn lg:hidden ml-auto"
              aria-label={t('common.close', 'Zamknij')}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="navscroll flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navGroups.map((group) => (
              <div key={group.label}>
                {!collapsed && <p className="navgroup__lbl">{group.label}</p>}
                {group.items.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    end={item.end}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `navitem ${isActive ? 'is-active' : ''} ${collapsed ? 'justify-center' : ''}`
                    }
                    title={collapsed ? item.name : undefined}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    {!collapsed && item.name}
                  </NavLink>
                ))}
              </div>
            ))}

            {/* Konto demo — członkostwo nadaje baza każdemu z zespołu partnera
                (migracja 883); bez niego pozycji nie ma. */}
            {firmaPokazowa.organizationId && (
              <div className="pt-4 mt-4 border-t border-aurora-border">
                <button
                  type="button"
                  onClick={otworzFirmePokazowa}
                  title={
                    collapsed
                      ? t('partner.nav.demoAccount', 'Konto demo')
                      : (firmaPokazowa.nazwa ?? undefined)
                  }
                  className={`navitem w-full ${collapsed ? 'justify-center' : ''}`}
                  data-testid="partner-konto-demo"
                >
                  <MonitorPlay className="w-5 h-5 flex-shrink-0" />
                  {!collapsed && t('partner.nav.demoAccount', 'Konto demo')}
                </button>
              </div>
            )}

            {/* Back to Admin — only for accounts that actually have a tenant-admin context */}
            {canReturnToAdmin && (
              <div className="pt-4 mt-4 border-t border-aurora-border">
                <NavLink
                  to="/admin"
                  onClick={() => setSidebarOpen(false)}
                  title={collapsed ? t('nav.backToAdmin', 'Powrót do Admina') : undefined}
                  className={`navitem ${collapsed ? 'justify-center' : ''}`}
                >
                  <ChevronLeft className="w-5 h-5 flex-shrink-0" />
                  {!collapsed && t('nav.backToAdmin', 'Powrót do Admina')}
                </NavLink>
              </div>
            )}
          </nav>

          {/* Collapse Toggle (desktop only) */}
          <div className="hidden lg:block px-2 py-2 border-t border-aurora-border">
            <button
              onClick={toggleCollapse}
              className={`navitem ${collapsed ? 'justify-center' : ''}`}
              title={
                collapsed ? t('nav.expandMenu', 'Rozwiń menu') : t('nav.collapseMenu', 'Zwiń menu')
              }
            >
              {collapsed ? (
                <PanelLeftOpen className="w-5 h-5 flex-shrink-0" />
              ) : (
                <>
                  <PanelLeftClose className="w-5 h-5 flex-shrink-0" />
                  <span>{t('nav.collapseMenu', 'Zwiń menu')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${collapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        {/* Nagłówek kolumny treści: pasek podglądu (tylko gdy super-admin
            przegląda panel jako partner) i topbar przyklejone RAZEM, jeden
            wiersz pod drugim. Pasek stał wcześniej w korzeniu layoutu, poza
            kolumną treści — leżał na logo paska bocznego, a po przewinięciu
            na okruszkach i wyszukiwarce. */}
        <div className="sticky top-0 z-30">
          <ImpersonationBanner />
          {/* Top Bar */}
          <header className="topbar z-30">
            <button
              onClick={() => setSidebarOpen(true)}
              className="iconbtn lg:hidden"
              aria-label="Otwórz menu"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="topbar__crumbs hidden sm:flex">
              Partner<span>›</span>
              <b>{currentPage}</b>
            </div>

            <button
              type="button"
              className="cmdk hidden md:flex"
              onClick={() => setPaletteOpen(true)}
              aria-label={t('partner.topbar.searchPlaceholder', 'Szukaj klienta, prowizji…')}
            >
              <SearchIcon />
              <span>{t('partner.topbar.searchPlaceholder', 'Szukaj klienta, prowizji…')}</span>
              <kbd>⌘K</kbd>
            </button>

            <div className="flex-1" />

            <div className="topbar__right gap-3">
              {/* .rolebadge — kolor/tło z tokenów roli (.role-partner), nie z utilities */}
              <span className="rolebadge">
                <Handshake className="w-3.5 h-3.5" />
                {t('roles.partner', 'Partner')}
              </span>
              {/* Wejście na konto klienta — na stałe w pasku, bo potrzeba pomocy
                „u klienta na ekranie" pojawia się na każdym ekranie panelu,
                a nie tylko na liście klientów. */}
              <WejscieNaKontoKlienta wariant="topbar" partnerOrgId={partnerOrgId} />
              <LanguageSwitcher variant="minimal" />
              {/* Parytet z AdminLayout — przełącznik motywu przed avatarem. */}
              <ThemeToggle size="sm" />
              {/* Jedyne miejsce zalogowanego użytkownika (parytet z Admin/SuperAdmin) */}
              <UserMenu
                imie={user?.full_name}
                email={user?.email}
                rola={t('roles.partner', 'Partner')}
                ustawieniaHref="/partner/settings"
                onSignOut={handleSignOut}
                inicjalZapasowy="P"
              />
            </div>
          </header>
        </div>

        {/* Page Content */}
        <main className="p-6">
          {/* Leniwy kawałek strony czeka W TREŚCI: pasek boczny i nagłówek
            zostają. Bez tej granicy czekanie trafiało do korzeniowego
            `Suspense` w `App.tsx`, który zdejmuje cały layout na rzecz
            pełnoekranowego znaku startu — przy każdym pierwszym wejściu
            w trasę z osobnego kawałka. */}
          <Suspense fallback={<EkranStartowy uklad="w-tresci" />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Paleta poleceń ⌘K — nawigacja po panelu partnera (Aurora DS) */}
      <CommandPalette items={navigation} open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
